import React, { useMemo } from 'react'
import classNames from 'classnames'
import astro from '../common/lib/astro'
import { HighchartsTemperatureGraph } from '../features/common/'
import { DayForecast, ForecastCurrentConditions } from './'
import { getAstroValue, getUserUnitI, timeFormatForUser } from '../common/ambient'
import { getDailyForecastLimit } from '../features/payment'
import PropTypes from 'prop-types'

export default function ForecastLongTermV2 ({ forecast, user, onClose, label }) {
  // Temperature graph data - first 24 hours
  const temperatureGraphData = useMemo(() => {
    if (!forecast?.hourly?.data) return []
    return forecast.hourly.data.slice(0, 24).map(hour => ({
      value: Math.round(hour.temperature),
      time: hour.time,
      label: moment(hour.time * 1000).format(
        getUserUnitI('hour24', user) === 1 ? 'H' : 'ha'
      ),
      icon: hour.icon
    }))
  }, [forecast, user])

  // Daily forecasts - filtered and limited
  const dailyForecasts = useMemo(() => {
    if (!forecast?.daily?.data) return []
    const days = forecast.daily.data.filter(f =>
      moment(f.time * 1000).startOf('day').valueOf() >=
      moment().startOf('day').valueOf()
    )
    return days?.slice(0, getDailyForecastLimit(user)) || []
  }, [forecast, user])

  // Sunrise/sunset times
  const sunTimes = useMemo(() => {
    if (!forecast) return { sunrise: '--', sunset: '--' }
    const getValue = getAstroValue(moment, {
      lat: forecast.lat,
      lon: forecast.lon,
      tz: forecast.tz
    })
    return {
      sunrise: getValue(astro.solar.rise, Date.now()).format(timeFormatForUser(user)),
      sunset: getValue(astro.solar.set, Date.now()).format(timeFormatForUser(user))
    }
  }, [forecast, user])

  if (!forecast) {
    return null
  }

  const { currently = {}, daily = {} } = forecast

  return (
    <div className='component-forecast-long-term-v2' onClick={e => e.stopPropagation()}>
      <a className='close' onClick={onClose} />

      {/* Header with location */}
      {label && <div className='location-header'>{label}</div>}

      {/* Temperature Graph Section */}
      <div className='temperature-graph-section'>
        <HighchartsTemperatureGraph
          data={temperatureGraphData}
          height={180}
        />
      </div>

      <div className='bottom-section'>
        <ForecastCurrentConditions
          data={currently}
          mode="current"
          dailyHigh={dailyForecasts[0]?.temperatureMax}
          dailyLow={dailyForecasts[0]?.temperatureMin}
          sunTimes={sunTimes}
          dailySummary={daily.summary}
        />


        <div className='forecast-grid-wrap'>
          <div className='five-day-forecast'>
            {dailyForecasts.map((f, i) => {
              const dayOfWeek = moment(f.time * 1000).day()
              const isWeekend = dayOfWeek === 0 || dayOfWeek === 6 // Sunday or Saturday
              return (
                <DayForecast
                  key={`forecast-${f.time}`}
                  className={isWeekend ? 'alt' : ''}
                  title={
                    <>
                      <span className='day'>{moment(f.time * 1000).format('ddd')}</span>
                      <span className='date'>{moment(f.time * 1000).format('M/D')}</span>
                    </>
                  }
                  forecast={{ ...f, int: true }}
                />
              )
            })}
          </div>
        </div>

      </div>

      <p>
        Weather forecast by <span className='accuweather-logo' />
      </p>
    </div>
  )
}

ForecastLongTermV2.propTypes = {
  forecast: PropTypes.object,
  user: PropTypes.object.isRequired,
  onClose: PropTypes.func,
  label: PropTypes.string
}

ForecastLongTermV2.displayName = 'ForecastLongTermV2'
